/**
 * Eén sleep-implementatie voor alle sorteerbare rijen (investeringstabel, prijsopbouw).
 *
 * Twee regels maken het verschil tussen "werkt" en "de rij blijft aan de muis plakken":
 *
 * 1. Tijdens het slepen raken we géén DOM aan die door iemand anders wordt beheerd.
 *    In ProseMirror leidt een class op een <tr> ertoe dat de rij opnieuw getekend wordt
 *    (en de sleepknop dus verdwijnt); in React zou het een re-render per muisbeweging
 *    kosten. De feedback — bronrij dimmen en de invoeglijn — tekenen we daarom als
 *    losse overlays op document.body.
 * 2. We luisteren op window in de capture-fase in plaats van op de sleepknop zelf.
 *    Zo eindigt de sleep ook als de knop tussendoor opnieuw getekend wordt of als je
 *    de muis buiten de lijst loslaat.
 */

/** Tekst op het sleepkaartje dat de muis volgt. */
export type RowDragGhost = {
  title: string;
  details?: string;
};

export type RowDragAdapter<Row> = {
  /** CSS-selector van een rij die als doel kan dienen. */
  rowSelector: string;
  /** Allow handles in the margin to target the nearest row. */
  gutter?: number;
  /** Rij-identiteit uit een DOM-element; null = geen geldig doel. */
  resolve: (rowElement: HTMLElement) => Row | null;
  /** Zijn dit dezelfde rijen? */
  sameRow: (a: Row, b: Row) => boolean;
  /** Mag de bronrij op dit doel vallen (zelfde tabel of lijst)? */
  canDrop: (source: Row, target: Row) => boolean;
  /** Actuele element bij een rij; opnieuw opzoeken overleeft een hertekening. */
  element: (row: Row) => HTMLElement | null;
  /** Inhoud van het sleepkaartje. */
  ghost: (row: Row) => RowDragGhost;
  /** Definitieve verplaatsing doorvoeren. */
  commit: (source: Row, target: Row, insertAfter: boolean) => void;
};

export type RowDragSorter<Row> = {
  /** Aanroepen vanaf pointerdown op de sleepknop. */
  start: (event: PointerEvent, source: Row) => void;
  /** Sleep afbreken zonder te verplaatsen. */
  cancel: () => void;
  /** Opruimen bij unmount of bij het vernietigen van de editor. */
  destroy: () => void;
};

/** Minimale muisbeweging voordat een klik als sleep telt. */
const DRAG_THRESHOLD_PX = 4;
/** Afstand van het sleepkaartje tot de cursor. */
const GHOST_OFFSET_PX = 14;

const ICON_GRIP =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>';

/**
 * Pure herordening zodat slepen en de toetsenbordacties dezelfde invoeglogica volgen.
 * `insertAfter` bepaalt of de rij vóór of ná het doel belandt.
 */
export function reorderRowsForDrop<T>(
  rows: readonly T[],
  sourceIndex: number,
  targetIndex: number,
  insertAfter: boolean,
): T[] {
  if (
    sourceIndex < 0 ||
    sourceIndex >= rows.length ||
    targetIndex < 0 ||
    targetIndex >= rows.length ||
    sourceIndex === targetIndex
  ) {
    return [...rows];
  }

  const next = [...rows];
  const [moved] = next.splice(sourceIndex, 1);
  let insertionIndex = targetIndex + (insertAfter ? 1 : 0);
  if (sourceIndex < insertionIndex) insertionIndex -= 1;
  next.splice(insertionIndex, 0, moved);
  return next;
}

export function createRowDragSorter<Row>(adapter: RowDragAdapter<Row>): RowDragSorter<Row> {
  type DragState = {
    pointerId: number;
    startX: number;
    startY: number;
    source: Row;
    target: Row | null;
    insertAfter: boolean;
    ghost: HTMLElement | null;
    sourceOverlay: HTMLElement | null;
    dropLine: HTMLElement | null;
    dragging: boolean;
  };

  let dragState: DragState | null = null;
  let listening = false;

  const makeOverlay = (className: string) => {
    const element = document.createElement('div');
    element.className = className;
    document.body.appendChild(element);
    return element;
  };

  const makeGhost = (content: RowDragGhost) => {
    const ghost = makeOverlay('row-drag-ghost');

    const icon = document.createElement('span');
    icon.className = 'row-drag-ghost-icon';
    icon.innerHTML = ICON_GRIP;

    const copy = document.createElement('span');
    copy.className = 'row-drag-ghost-copy';
    const title = document.createElement('strong');
    title.textContent = content.title;
    const details = document.createElement('span');
    details.textContent = content.details || '';
    copy.append(title, details);

    ghost.append(icon, copy);
    return ghost;
  };

  const rowElementAtPoint = (clientX: number, clientY: number): HTMLElement | null => {
    let element = document.elementFromPoint(clientX, clientY);
    if (!element?.closest(adapter.rowSelector) && adapter.gutter && dragState) {
      const rect = adapter.element(dragState.source)?.getBoundingClientRect();
      if (rect && clientX >= rect.left - adapter.gutter && clientX <= rect.right + adapter.gutter) {
        element = document.elementFromPoint(Math.min(rect.right - 1, Math.max(rect.left + 1, clientX)), clientY);
      }
    }
    if (!(element instanceof Element)) return null;
    const row = element.closest(adapter.rowSelector);
    return row instanceof HTMLElement ? row : null;
  };

  /** Bronrij dimmen en de invoeglijn tekenen, allebei buiten de beheerde DOM. */
  const paintOverlays = () => {
    const state = dragState;
    if (!state?.dragging) return;

    if (state.sourceOverlay) {
      const sourceElement = adapter.element(state.source);
      if (sourceElement) {
        const rect = sourceElement.getBoundingClientRect();
        state.sourceOverlay.style.display = 'block';
        state.sourceOverlay.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0)`;
        state.sourceOverlay.style.width = `${rect.width}px`;
        state.sourceOverlay.style.height = `${rect.height}px`;
      } else {
        state.sourceOverlay.style.display = 'none';
      }
    }

    if (!state.dropLine) return;
    const targetElement = state.target ? adapter.element(state.target) : null;
    if (!targetElement) {
      state.dropLine.style.display = 'none';
      return;
    }
    const rect = targetElement.getBoundingClientRect();
    const y = (state.insertAfter ? rect.bottom : rect.top) - 1.5;
    state.dropLine.style.display = 'block';
    state.dropLine.style.transform = `translate3d(${rect.left}px, ${y}px, 0)`;
    state.dropLine.style.width = `${rect.width}px`;
  };

  const updateDropTarget = (clientX: number, clientY: number) => {
    const state = dragState;
    if (!state?.dragging) return;

    const targetElement = rowElementAtPoint(clientX, clientY);
    const target = targetElement ? adapter.resolve(targetElement) : null;
    if (
      !targetElement ||
      !target ||
      adapter.sameRow(target, state.source) ||
      !adapter.canDrop(state.source, target)
    ) {
      state.target = null;
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    state.target = target;
    state.insertAfter = clientY >= rect.top + rect.height / 2;
  };

  const beginDragging = (state: DragState, clientX: number, clientY: number) => {
    if (!adapter.element(state.source)) return;
    state.dragging = true;
    state.ghost = makeGhost(adapter.ghost(state.source));
    state.sourceOverlay = makeOverlay('row-drag-source');
    state.dropLine = makeOverlay('row-drag-line');
    document.body.classList.add('is-row-dragging');
    state.ghost.style.transform = `translate3d(${clientX + GHOST_OFFSET_PX}px, ${clientY + GHOST_OFFSET_PX}px, 0)`;
  };

  const cleanup = () => {
    if (!dragState) return;
    dragState.ghost?.remove();
    dragState.sourceOverlay?.remove();
    dragState.dropLine?.remove();
    document.body.classList.remove('is-row-dragging');
    dragState = null;
    stopListening();
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    // Knop buiten het venster losgelaten: de pointerup kwam nooit aan, dus stoppen we
    // bij de eerste beweging waarin geen knop meer ingedrukt is.
    if (event.pointerType !== 'touch' && event.buttons === 0) {
      cleanup();
      return;
    }

    if (!dragState.dragging) {
      const distance = Math.hypot(
        event.clientX - dragState.startX,
        event.clientY - dragState.startY,
      );
      if (distance < DRAG_THRESHOLD_PX) return;
      beginDragging(dragState, event.clientX, event.clientY);
      if (!dragState.dragging) {
        cleanup();
        return;
      }
    }

    event.preventDefault();
    if (dragState.ghost) {
      dragState.ghost.style.transform = `translate3d(${event.clientX + GHOST_OFFSET_PX}px, ${event.clientY + GHOST_OFFSET_PX}px, 0)`;
    }
    updateDropTarget(event.clientX, event.clientY);
    paintOverlays();
  };

  const onPointerUp = (event: PointerEvent) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    event.preventDefault();
    const state = dragState;
    if (state.dragging && state.target) {
      adapter.commit(state.source, state.target, state.insertAfter);
    }
    cleanup();
  };

  const onPointerCancel = (event: PointerEvent) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    cleanup();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!dragState || event.key !== 'Escape') return;
    event.preventDefault();
    cleanup();
  };

  /**
   * Tab of venster verdwijnt tijdens het slepen: afbreken i.p.v. een zwevend kaartje.
   * Losse blur-events zijn hiervoor ongeschikt — een niet-gefocust venster (webview,
   * iframe, tweede scherm) stuurt die continu en zou elke sleep meteen afbreken.
   */
  const onVisibilityChange = () => {
    if (document.hidden) cleanup();
  };
  const onScroll = () => paintOverlays();

  const startListening = () => {
    if (listening) return;
    listening = true;
    // Capture-fase: geen enkele tussenliggende handler kan het einde van de sleep opeten.
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp, true);
    window.addEventListener('pointercancel', onPointerCancel, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('visibilitychange', onVisibilityChange);
  };

  function stopListening() {
    if (!listening) return;
    listening = false;
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', onPointerUp, true);
    window.removeEventListener('pointercancel', onPointerCancel, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('scroll', onScroll, true);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  return {
    start(event, source) {
      if (
        dragState ||
        !event.isPrimary ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }

      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        source,
        target: null,
        insertAfter: false,
        ghost: null,
        sourceOverlay: null,
        dropLine: null,
        dragging: false,
      };

      startListening();
      event.preventDefault();
      event.stopPropagation();
    },

    cancel: cleanup,

    destroy() {
      cleanup();
      stopListening();
    },
  };
}
